<script setup lang="ts" generic="Multiple extends boolean = false">
import type { ComponentPublicInstance } from 'vue';

import { computed, nextTick, ref, useId } from 'vue';

import { moveFocusFromTrigger } from '@/shared/lib';

import type { FilterOptionValue, FilterSelectOption } from './filter';

import { BaseDropdown } from '../dropdown';
import { IconCheck, IconChevronDown } from '../icons';
import { AppSpinner } from '../spinner';

type FilterModelValue = Multiple extends true ? FilterOptionValue[] : FilterOptionValue | null;

interface Props {
  label: string;
  options: readonly FilterSelectOption[];
  multiple?: Multiple;
  loading?: boolean;
  disabled?: boolean;
  emptyText?: string;
  clearText?: string;
  width?: string;
}

const props = defineProps<Props>();

const modelValue = defineModel<FilterModelValue>({ required: true });

const triggerRef = ref<HTMLButtonElement | null>(null);
const listboxRef = ref<HTMLDivElement | null>(null);
const optionRefs = ref<Array<HTMLButtonElement | null>>([]);
const listboxId = `filter-select-${useId()}`;
const isMultiple = computed(() => props.multiple === true || (props.multiple as unknown) === '');

defineSlots<{
  option(props: { option: FilterSelectOption; selected: boolean }): unknown;
}>();

const selectedValues = computed<FilterOptionValue[]>(() => {
  if (isMultiple.value) {
    return Array.isArray(modelValue.value) ? modelValue.value : [];
  }

  return modelValue.value == null || Array.isArray(modelValue.value) ? [] : [modelValue.value];
});

const selectedCount = computed(() => selectedValues.value.length);
const hasSelection = computed(() => selectedCount.value > 0);

const triggerLabel = computed(() => {
  if (selectedCount.value === 0) {
    return props.label;
  }

  if (selectedCount.value === 1) {
    const selectedOption = props.options.find((option) => option.value === selectedValues.value[0]);
    return `${props.label}: ${selectedOption?.label ?? '—'}`;
  }

  return props.label;
});

const isSelected = (value: FilterOptionValue): boolean => selectedValues.value.includes(value);

const getEnabledOptionIndexes = (): number[] =>
  props.options.flatMap((option, index) => (option.disabled ? [] : [index]));

const setOptionRef = (element: Element | ComponentPublicInstance | null, index: number): void => {
  optionRefs.value[index] = element instanceof HTMLButtonElement ? element : null;
};

const focusOption = (index: number | undefined): void => {
  if (index == null) {
    listboxRef.value?.focus();
    return;
  }

  optionRefs.value[index]?.focus();
};

const getInitialOptionIndex = (preferLast = false): number | undefined => {
  const selectedIndex = props.options.findIndex(
    (option) => !option.disabled && selectedValues.value.includes(option.value),
  );

  if (selectedIndex !== -1) {
    return selectedIndex;
  }

  const enabledIndexes = getEnabledOptionIndexes();
  return preferLast ? enabledIndexes.at(-1) : enabledIndexes[0];
};

const focusInitialOption = (preferLast = false): void => {
  void nextTick(() => {
    requestAnimationFrame(() => focusOption(getInitialOptionIndex(preferLast)));
  });
};

const handleTriggerClick = (toggle: () => void, isOpen: boolean): void => {
  toggle();
  if (!isOpen) {
    focusInitialOption();
  }
};

const handleTriggerKeydown = (event: KeyboardEvent, toggle: () => void, isOpen: boolean): void => {
  const focusByKey = (preferLast = false): void => {
    event.preventDefault();
    if (!isOpen) {
      toggle();
    }
    focusInitialOption(preferLast);
  };

  if (event.key === 'ArrowDown' || event.key === 'Home') {
    focusByKey();
    return;
  }

  if (event.key === 'ArrowUp' || event.key === 'End') {
    focusByKey(true);
    return;
  }

  if (event.key === 'Escape' && isOpen) {
    event.preventDefault();
    toggle();
    void nextTick(() => triggerRef.value?.focus());
  }
};

const focusRelativeOption = (direction: 1 | -1): void => {
  const enabledIndexes = getEnabledOptionIndexes();
  if (enabledIndexes.length === 0) {
    listboxRef.value?.focus();
    return;
  }

  const currentIndex = optionRefs.value.findIndex((option) => option === document.activeElement);
  const currentEnabledIndex = enabledIndexes.indexOf(currentIndex);
  const nextEnabledIndex =
    currentEnabledIndex === -1
      ? direction === 1
        ? 0
        : enabledIndexes.length - 1
      : (currentEnabledIndex + direction + enabledIndexes.length) % enabledIndexes.length;

  focusOption(enabledIndexes[nextEnabledIndex]);
};

const closeAndFocusTrigger = (close: () => void): void => {
  close();
  void nextTick(() => triggerRef.value?.focus());
};

const handleListboxKeydown = (event: KeyboardEvent, close: () => void): void => {
  if (event.key === 'Enter' || event.key === ' ') {
    const currentIndex = optionRefs.value.findIndex((option) => option === document.activeElement);
    const currentOption = optionRefs.value[currentIndex];

    if (currentOption) {
      event.preventDefault();
      currentOption.click();
    }
    return;
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    focusRelativeOption(1);
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    focusRelativeOption(-1);
    return;
  }

  if (event.key === 'Home' || event.key === 'End') {
    event.preventDefault();
    const enabledIndexes = getEnabledOptionIndexes();
    focusOption(event.key === 'Home' ? enabledIndexes[0] : enabledIndexes.at(-1));
    return;
  }

  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    close();
    void nextTick(() => triggerRef.value?.focus());
    return;
  }

  if (event.key === 'Tab') {
    event.preventDefault();
    close();
    void nextTick(() => moveFocusFromTrigger(triggerRef.value, event.shiftKey));
  }
};

const clearSelection = (close: () => void): void => {
  if (isMultiple.value) {
    modelValue.value = [] as unknown as FilterModelValue;
    return;
  }

  modelValue.value = null as FilterModelValue;
  closeAndFocusTrigger(close);
};

const selectOption = (option: FilterSelectOption, close: () => void): void => {
  if (option.disabled || props.loading || props.disabled) {
    return;
  }

  if (isMultiple.value) {
    const nextValues = [...selectedValues.value];
    const selectedIndex = nextValues.indexOf(option.value);

    if (selectedIndex === -1) {
      nextValues.push(option.value);
    } else {
      nextValues.splice(selectedIndex, 1);
    }

    modelValue.value = nextValues as FilterModelValue;
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- FilterModelValue зависит от Multiple, без приведения не компилируется.
  modelValue.value = option.value as FilterModelValue;
  closeAndFocusTrigger(close);
};
</script>

<template>
  <BaseDropdown
    :width="width ?? 'min(280px, calc(100vw - 2rem))'"
    trigger-class="w-full sm:w-auto"
    :content="{ align: 'start', side: 'bottom', sideOffset: 8 }"
  >
    <template #trigger="{ isOpen, toggle }">
      <button
        ref="triggerRef"
        type="button"
        class="flex h-11 w-full min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-(--base-secondary-color) px-4 text-sm text-(--base-text-color) transition-[background-color,box-shadow,transform] duration-150 hover:bg-(--base-main-bg) focus-visible:ring-2 focus-visible:ring-(--base-accent-color) focus-visible:outline-none active:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
        :disabled="disabled"
        aria-haspopup="listbox"
        :aria-expanded="isOpen"
        :aria-controls="listboxId"
        @click="handleTriggerClick(toggle, isOpen)"
        @keydown="handleTriggerKeydown($event, toggle, isOpen)"
      >
        <span class="min-w-0 flex-1 truncate text-left sm:flex-initial">{{ triggerLabel }}</span>
        <span
          v-if="hasSelection && isMultiple"
          class="shrink-0 rounded-full bg-(--base-accent-color)/15 px-1.5 py-px text-xs leading-tight text-(--base-accent-color) tabular-nums"
        >
          {{ selectedCount }}
        </span>
        <IconChevronDown
          :width="10"
          :height="10"
          class="shrink-0 transition-transform duration-150"
          :class="{ 'rotate-180': isOpen }"
        />
      </button>
    </template>

    <template #content="{ close }">
      <div
        :id="listboxId"
        ref="listboxRef"
        role="listbox"
        :aria-label="label"
        :aria-multiselectable="isMultiple ? 'true' : undefined"
        :aria-busy="loading"
        tabindex="-1"
        class="flex flex-col py-1 outline-none"
        @keydown="handleListboxKeydown($event, close)"
      >
        <button
          v-if="hasSelection"
          type="button"
          class="min-h-11 cursor-pointer px-3 py-2 text-left text-xs font-medium text-(--base-accent-color) transition-colors hover:bg-(--base-popup-hover-color) focus-visible:ring-2 focus-visible:ring-(--base-accent-color) focus-visible:outline-none focus-visible:ring-inset"
          @click="clearSelection(close)"
        >
          {{ clearText ?? 'Сбросить' }}
        </button>
        <div
          v-if="hasSelection"
          class="h-px bg-(--base-popup-border-color)"
        />

        <div
          v-if="loading"
          role="status"
          aria-live="polite"
          class="flex min-h-20 items-center justify-center gap-2 px-4 py-3 text-sm text-(--base-popup-secondary-color)"
        >
          <AppSpinner size="sm" />
          <span>Загрузка вариантов…</span>
        </div>

        <p
          v-else-if="options.length === 0"
          class="px-4 py-3 text-sm text-(--base-popup-secondary-color)"
        >
          {{ emptyText ?? 'Нет вариантов' }}
        </p>

        <button
          v-for="(option, optionIndex) in options"
          v-else
          :key="option.value"
          :ref="(element) => setOptionRef(element, optionIndex)"
          type="button"
          role="option"
          class="flex min-h-11 w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-(--base-popup-text-color) transition-colors hover:bg-(--base-popup-hover-color) focus-visible:ring-2 focus-visible:ring-(--base-accent-color) focus-visible:outline-none focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="option.disabled"
          :aria-selected="isSelected(option.value)"
          :aria-disabled="option.disabled || undefined"
          @click="selectOption(option, close)"
        >
          <span
            aria-hidden="true"
            class="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border"
            :class="
              isSelected(option.value)
                ? 'border-(--base-accent-color) bg-(--base-accent-color) text-white'
                : 'border-(--base-secondary-color)'
            "
          >
            <IconCheck
              v-if="isSelected(option.value)"
              :width="12"
              :height="10"
            />
          </span>
          <slot
            name="option"
            :option="option"
            :selected="isSelected(option.value)"
          />
          <span class="min-w-0 flex-1 truncate">{{ option.label }}</span>
        </button>
      </div>
    </template>
  </BaseDropdown>
</template>
